'use client';
import { useCallback, useEffect, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

export type ScrollDirection = 'up' | 'down' | 'none';

export interface UseScrollDirectionReturn {
  scrollDirection: ScrollDirection;
  scrollY: number;
  isScrolledPast: boolean[];
}

/**
 * Hook to track scroll direction and position
 * @param thresholds - Array of pixel values to track whether user has scrolled past each threshold
 * @returns Object containing scroll direction, scroll position, and array of boolean values for each threshold
 */
export function useScrollDirection(
  thresholds: number[] = [500]
): UseScrollDirectionReturn {
  const [scrollDirection, setScrollDirection] =
    useState<ScrollDirection>('none');
  const [scrollY, setScrollY] = useState(0);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isScrolledPast, setIsScrolledPast] = useState<boolean[]>(
    new Array(thresholds.length).fill(false)
  );

  // Debounced scroll handler for better performance
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;

    setScrollY(currentScrollY);
    setIsScrolledPast(
      thresholds.map((threshold) => currentScrollY > threshold)
    );

    // Only update direction if we've scrolled more than a few pixels
    // This prevents jittery behavior from small scroll changes
    const scrollDifference = Math.abs(currentScrollY - lastScrollY);

    if (scrollDifference > 3) {
      if (currentScrollY > lastScrollY && currentScrollY > 10) {
        setScrollDirection('down');
      } else if (currentScrollY < lastScrollY) {
        setScrollDirection('up');
      }
      setLastScrollY(currentScrollY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastScrollY]); // thresholds intentionally omitted - they never change

  // Debounce the scroll handler to improve performance
  const debouncedHandleScroll = useDebounceCallback(handleScroll, 5);

  useEffect(() => {
    // Set initial scroll position
    const initialScrollY = window.scrollY;
    setScrollY(initialScrollY);
    setLastScrollY(initialScrollY);
    setIsScrolledPast(
      thresholds.map((threshold) => initialScrollY > threshold)
    );

    // Add scroll listener with passive option for better performance
    window.addEventListener('scroll', debouncedHandleScroll, { passive: true });

    // Cleanup
    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedHandleScroll]); // thresholds intentionally omitted - they never change

  return {
    scrollDirection,
    scrollY,
    isScrolledPast,
  };
}
